import React, { useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import CookieUtils from "../../../utils/cookie-utils";
import FaultTicketService, {
  FaultTicket,
  FAULT_SEVERITY_LABEL,
  FAULT_STATUS,
  FAULT_STATUS_LABEL,
  FAULT_SYNC_STATUS,
  FAULT_SOURCE_LABEL,
  FAULT_TYPE_LABEL,
  SHORTAGE_REASON_LABEL,
  formatDateTime,
  formatMinutes,
  getOverdueMinutes,
} from "../services/fault-ticket-service";

interface Props {
  open: boolean;
  ticket: FaultTicket | null;
  onClose: () => void;
  onUpdated: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 font-medium text-right">{value}</span>
  </div>
);

const FaultTicketDetailModal: React.FC<Props> = ({ open, ticket, onClose, onUpdated }) => {
  const { fetch: updateFault, loading } = FaultTicketService.update();
  const [status, setStatus] = useState<number>(ticket?.status ?? FAULT_STATUS.OPEN);
  const [penalty, setPenalty] = useState<string>(
    ticket?.penalty_amount != null ? Number(ticket.penalty_amount).toLocaleString("vi-VN") : ""
  );

  // Sync khi đổi ticket
  React.useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setPenalty(
        ticket.penalty_amount != null ? Number(ticket.penalty_amount).toLocaleString("vi-VN") : ""
      );
    }
  }, [ticket?.id]);

  if (!open || !ticket) return null;

  // Phiếu đã chốt ("Đã ghi lỗi"/"Hủy lỗi") → chỉ owner (token có ADMIN) được sửa.
  const privilegeCodes: string[] = CookieUtils.getCurrentUser()?.privilege_codes ?? [];
  const isOwner = privilegeCodes.includes("ADMIN");
  const isTerminal =
    ticket.status === FAULT_STATUS.RESOLVED || ticket.status === FAULT_STATUS.REJECTED;
  const readOnly = isTerminal && !isOwner;

  const handleSave = async () => {
    try {
      const res = await updateFault(ticket.id, {
        status,
        penalty_amount: penalty ? Number(penalty.replace(/\D/g, "")) : undefined,
      });
      if (res && (res.status === 200 || res.status === undefined)) {
        showToast({ type: "success", message: "Đã cập nhật phiếu lỗi" });
        onUpdated();
        onClose();
      } else {
        showToast({ type: "error", message: res?.message || "Cập nhật thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Cập nhật thất bại" });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[520px] max-w-[95vw] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Phiếu lỗi {ticket.code}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-lg">
            ✕
          </button>
        </div>

        <div className="divide-y">
          <Row label="Chi nhánh" value={ticket.branch_name || `#${ticket.branch_id}`} />
          <Row
            label="Đơn liên quan"
            value={ticket.order_code || (ticket.order_id ? `#${ticket.order_id}` : "—")}
          />
          <Row label="Loại lỗi" value={FAULT_TYPE_LABEL[ticket.fault_type] || ticket.fault_type} />
          {ticket.milestone_name && (
            <Row
              label="Mốc SLA"
              value={`${ticket.milestone_name}${ticket.escalation_level ? ` · Lần ${ticket.escalation_level}` : ""}`}
            />
          )}
          {ticket.penalty_points != null && (
            <Row label="Điểm phạt" value={`${ticket.penalty_points} điểm`} />
          )}
          {/* Cửa sổ THỰC HIỆN BƯỚC — căn cứ phạt, để nhân sự giải trình và giám sát duyệt nhìn cùng con số. */}
          {ticket.sla_from_at && (
            <Row
              label="Thực hiện bước"
              value={
                <span>
                  {formatDateTime(ticket.sla_from_at)} →{" "}
                  {ticket.sla_to_at ? (
                    formatDateTime(ticket.sla_to_at)
                  ) : (
                    <span className="text-amber-600">chưa hoàn thành</span>
                  )}
                </span>
              }
            />
          )}
          {ticket.sla_elapsed_minutes != null && (
            <Row
              label="Vượt quy định"
              value={
                <span>
                  <span className="text-rose-600 font-semibold">
                    {formatMinutes(getOverdueMinutes(ticket) ?? 0)}
                  </span>
                  <span className="text-slate-400">
                    {" "}
                    (làm {formatMinutes(ticket.sla_elapsed_minutes)}
                    {ticket.sla_target_hours != null ? ` / quy định ${ticket.sla_target_hours}h` : ""})
                  </span>
                </span>
              }
            />
          )}
          <Row label="Mức độ" value={FAULT_SEVERITY_LABEL[ticket.severity] || ticket.severity} />
          <Row
            label="Nhân sự chịu trách nhiệm"
            value={ticket.responsible_name || `NV #${ticket.responsible_user_id}`}
          />
          <Row
            label="Người tạo"
            value={ticket.reporter_name || (ticket.reported_by ? `#${ticket.reported_by}` : "Hệ thống")}
          />
          <Row label="Nguồn" value={FAULT_SOURCE_LABEL[ticket.source_type] || ticket.source_type} />
          <Row
            label="Ngày tạo"
            value={formatDateTime(ticket.created_at)}
          />
        </div>

        {/* Căn cứ THẤT THOÁT ĐỒ — chặng nào, thiếu loại nào, so với số nào. Cùng vai trò với khối SLA ở trên:
            nhân sự giải trình và giám sát duyệt nhìn đúng con số hệ thống đã dùng lúc ra phiếu. */}
        {ticket.shortage_data && (
          <div className="mt-3 border border-rose-100 bg-rose-50 rounded-lg p-3">
            <div className="text-sm font-semibold text-rose-800 mb-2">Căn cứ thất thoát</div>
            <div className="divide-y divide-rose-100">
              <Row
                label="Chặng"
                value={`${ticket.shortage_data.stage_from_label} → ${ticket.shortage_data.stage_to_label}`}
              />
              <Row
                label="Số lượng"
                value={
                  <span>
                    <span className="text-slate-500">
                      {ticket.shortage_data.stage_from_label}: {ticket.shortage_data.expected_total} cái →{" "}
                      {ticket.shortage_data.stage_to_label}: {ticket.shortage_data.actual_total} cái
                    </span>
                    <span className="text-rose-600 font-semibold">
                      {" "}
                      (thiếu {ticket.shortage_data.missing_total})
                    </span>
                  </span>
                }
              />
              <Row
                label="Loại đồ thiếu"
                value={ticket.shortage_data.missing_items
                  .map((i) => `${i.category} ${i.quantity}`)
                  .join(", ")}
              />
              {ticket.shortage_data.reason && (
                <Row
                  label="Lý do NV chọn"
                  value={
                    SHORTAGE_REASON_LABEL[ticket.shortage_data.reason] || ticket.shortage_data.reason
                  }
                />
              )}
              {ticket.shortage_data.from_at && (
                <Row
                  label="Thời điểm"
                  value={
                    <span className="text-slate-500">
                      {formatDateTime(ticket.shortage_data.from_at)}
                      {ticket.shortage_data.to_at
                        ? ` → ${formatDateTime(ticket.shortage_data.to_at)}`
                        : ""}
                    </span>
                  }
                />
              )}
            </div>
          </div>
        )}

        {ticket.description && (
          <div className="mt-3">
            <div className="text-sm text-slate-500 mb-1">Mô tả</div>
            <div className="text-sm bg-slate-50 rounded-lg p-3 text-slate-700">{ticket.description}</div>
          </div>
        )}

        {ticket.explanation && (
          <div className="mt-3">
            <div className="text-sm text-slate-500 mb-1">Giải trình của nhân sự</div>
            <div className="text-sm bg-blue-50 rounded-lg p-3 text-slate-700">{ticket.explanation}</div>
          </div>
        )}

        {/* Phiếu đã đẩy sang đối tác: sửa ở đây KHÔNG cập nhật được bên kia (chưa có API điều chỉnh) */}
        {ticket.sync_status === FAULT_SYNC_STATUS.SENT && (
          <div className="mt-4 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
            <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="text-sm text-orange-800">
              <p className="font-medium">Phiếu đã đồng bộ sang hệ thống đối tác</p>
              <p className="mt-0.5">
                {ticket.synced_at ? `Gửi lúc ${formatDateTime(ticket.synced_at)}. ` : ""}
                Sửa trạng thái hoặc số tiền ở đây sẽ <strong>không</strong> cập nhật sang bên đối tác — cần báo họ điều chỉnh thủ công.
              </p>
            </div>
          </div>
        )}

        {/* Cập nhật xử lý */}
        <div className="mt-4 bg-amber-50 border border-amber-100 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-amber-800 mb-3">
            {readOnly ? "Kết quả xử lý" : "Xử lý phiếu"}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Trạng thái</label>
              <select
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                value={status}
                disabled={readOnly}
                onChange={(e) => setStatus(Number(e.target.value))}
              >
                {Object.entries(FAULT_STATUS_LABEL).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Tiền phạt</label>
              <input
                type="text"
                inputMode="numeric"
                value={penalty}
                disabled={readOnly}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setPenalty(digits ? Number(digits).toLocaleString("vi-VN") : "");
                }}
                placeholder="VD: 200.000"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-slate-50">
            Đóng
          </button>
          {!readOnly && (
            <button
              disabled={loading}
              onClick={handleSave}
              className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
            >
              {loading ? "Đang lưu..." : "Lưu"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaultTicketDetailModal;
